import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Skeleton, Tab, Tabs } from '@mui/material';
import { EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import PageContainer from '../../components/PageContainer';
import MockupGenerationLogsFilters from '../../components/MockupGenerationLogsFilters';
import MockupGenerationLogsTable from '../../components/MockupGenerationLogsTable';
import MockupGenerationLogsAnalytics from '../../components/MockupGenerationLogsAnalytics';
import { MockupGenerationLogs } from '../../store/slices';
import { IStore } from '../../store/types';
import { IMockupGenerationLogsQueryParams } from '../../api/mockupGenerationLogs';

const analyticsKey = (p: IMockupGenerationLogsQueryParams | null) => {
    if (!p) return '';
    const { page, limit, sortDirection, total, ...rest } = p;
    return JSON.stringify(rest);
};

const MockupGenerationLogsPage = () => {
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState(0);

    const {
        list, status, queryParams,
        analyticsRows, analyticsStatus, analyticsTotal, analyticsTruncated, analyticsParams,
    } = useSelector<IStore, IStore['mockupGenerationLogs']>((state) => state.mockupGenerationLogs);

    const loadLogs = (params: IMockupGenerationLogsQueryParams) => {
        dispatch<any>(MockupGenerationLogs.getMockupGenerationLogs(params));
    };

    useEffect(() => {
        loadLogs({ ...MockupGenerationLogs.DEFAULT_MOCKUP_GENERATION_LOGS_PARAMS(), ...queryParams, page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Analytics are computed client-side from all rows in the filter range, so reload them
    // only when a non-paging filter changes and only while the Analytics tab is visible.
    // analyticsStatus / analyticsParams are deps so that a filter change made while a
    // fetch is in flight is picked up as soon as that fetch settles.
    useEffect(() => {
        if (activeTab !== 1) return;
        if (analyticsStatus === 'loading') return;
        if (analyticsKey(analyticsParams) === analyticsKey(queryParams)) return;
        dispatch<any>(MockupGenerationLogs.getMockupGenerationLogsAnalytics(queryParams));
    }, [activeTab, queryParams, analyticsStatus, analyticsParams, dispatch]);

    const currentParams = { ...MockupGenerationLogs.DEFAULT_MOCKUP_GENERATION_LOGS_PARAMS(), ...queryParams };

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Mockups"
                title="Generation logs"
                badges={(currentParams.total || 0) > 0 ? <StatusChip tone="neutral">{(currentParams.total || 0).toLocaleString('en-US')} total</StatusChip> : undefined}
            >
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} aria-label="Logs and analytics">
                    <Tab label="Logs" />
                    <Tab label="Analytics" />
                </Tabs>
            </PageHeader>

            <Panel padding="none">
                <MockupGenerationLogsFilters
                    queryParams={currentParams}
                    loadLogs={loadLogs}
                    loading={status === 'loading'}
                />
                {activeTab === 0 && (
                    <>
                        {status === 'loading' && (
                            <div style={{ padding: 16, display: 'grid', gap: 10 }} role="status" aria-label="Loading logs">
                                {[0, 1, 2, 3, 4, 5].map((row) => <Skeleton key={row} animation="wave" height={44} />)}
                            </div>
                        )}
                        {status !== 'loading' && list?.length > 0 && (
                            <MockupGenerationLogsTable logs={list} paging={currentParams} pagingUpdate={loadLogs} />
                        )}
                        {status === 'resolved' && list?.length === 0 && (
                            <EmptyState title="No generation logs match these filters" hint="Widen the date range or clear a filter." />
                        )}
                        {status === 'rejected' && (
                            <EmptyState
                                title="Logs could not be loaded"
                                hint="The 3D server log endpoint did not answer. Try again in a moment."
                                action={<Button variant="outlined" size="small" onClick={() => loadLogs(currentParams)}>Retry</Button>}
                            />
                        )}
                    </>
                )}
                {activeTab === 1 && (
                    <div style={{ padding: 16 }}>
                        {analyticsStatus === 'loading' && (
                            <Skeleton animation="wave" width="100%" height={500} variant="rounded" />
                        )}
                        {analyticsStatus !== 'loading' && (
                            <MockupGenerationLogsAnalytics
                                rows={analyticsRows}
                                totalCount={analyticsTotal}
                                truncated={analyticsTruncated}
                            />
                        )}
                    </div>
                )}
            </Panel>
        </PageContainer>
    );
};

export default MockupGenerationLogsPage;
